import React, { useEffect } from 'react';
import { MessageCircle, X, Sparkles } from 'lucide-react';
import { usePageContext } from '@/hooks/usePageContext';
import { useSmartChat } from '@/hooks/useSmartChat';
import { setSmartChatActive } from '@/hooks/useCaretPosition';
import { ChatContainer } from './ChatContainer';
import { AnimatedFloatingButton } from '@/components/ui/animated-floating-button';
import { cn } from '@/lib/utils';

export const SmartChatWidget = () => {
  const { pageContext, isLoading: contextLoading } = usePageContext();
  const smartChat = useSmartChat();

  // Update page context when it changes - SEMPRE executar hooks primeiro
  useEffect(() => {
    smartChat.updatePageContext(pageContext);
  }, [pageContext, smartChat.updatePageContext]);

  // Comunicar estado do chat para useCaretPosition - SEMPRE executar hooks primeiro
  useEffect(() => {
    setSmartChatActive(smartChat.isOpen);
    
    // Cleanup ao desmontar
    return () => {
      setSmartChatActive(false);
    };
  }, [smartChat.isOpen]);

  const hasNewContext = pageContext && !contextLoading;
  const hasContextualSuggestions = smartChat.contextualSuggestions.length > 0;

  return (
    <div className="font-sans">
      {/* Main Chat Container */}
      {smartChat.isOpen && (
        <div className="mb-4 animate-slide-in-right">
          <ChatContainer
            messages={smartChat.messages}
            onSendMessage={smartChat.sendMessage}
            onSendSuggestion={smartChat.sendSuggestion}
            onClose={smartChat.closeChat}
            pageContext={smartChat.currentPageContext}
            contextualSuggestions={smartChat.contextualSuggestions}
            isLoading={smartChat.isLoading}
            isTyping={smartChat.isTyping}
            error={smartChat.error}
            onClearError={smartChat.clearError}
          />
        </div>
      )}

      {/* Toggle Button */}
      {!smartChat.isOpen && (
        <AnimatedFloatingButton
          icon={<MessageCircle className="w-6 h-6" />}
          label="Suporte"
          gradientFrom="#56CCF2"
          gradientTo="#2F80ED"
          onClick={smartChat.toggleChat}
          ariaLabel="Abrir chat de suporte"
        >
          {/* Background pulse effect for new context */}
          {hasNewContext && (
            <div className="absolute inset-0 rounded-full bg-primary/30 animate-pulse pointer-events-none" />
          )}
          
          {/* Context indicator */}
          {hasContextualSuggestions && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-accent rounded-full border-2 border-white pointer-events-none">
              <Sparkles className="w-2 h-2 m-0.5 text-white" />
            </div>
          )}
          
          {/* Loading indicator */}
          {contextLoading && (
            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-white/50 animate-spin pointer-events-none" />
          )}
        </AnimatedFloatingButton>
      )}
      
      {/* Close Button (quando chat está aberto) */}
      {smartChat.isOpen && (
        <button
          onClick={smartChat.closeChat}
          className={cn(
            "relative w-14 h-14 rounded-full shadow-lg transition-all duration-300 ease-out",
            "bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70",
            "border-2 border-white/20 backdrop-blur-sm",
            "hover:scale-110 hover:shadow-xl",
            "flex items-center justify-center text-white",
            "rotate-180"
          )}
          aria-label="Fechar chat"
        >
          <X className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};